import React, { useState } from 'react';
import Select from 'react-select';
import { useSelectNurses } from 'store';
import { SelectOption } from 'lib/components/types';
import classes from './index.module.scss';

/**
 * Props for the NurseSelect component
 */
interface Props {
	value?: number; // Currently selected nurse ID
	defaultValue?: SelectOption<number>; // Default value for the select component
	setValue: React.Dispatch<React.SetStateAction<number | undefined>>; // Callback to update selected nurse ID
}

/**
 * Dropdown component for selecting a nurse
 * Used in job creation and editing forms to assign a nurse to a job
 *
 * @param value - Currently selected nurse ID
 * @param setValue - Callback to update selected nurse ID
 * @param defaultValue - Default value for the select component
 */
const NurseSelect = ({ value, setValue, defaultValue }: Props) => {
	// Get nurses data from Redux store
	const nurses = useSelectNurses();

	/**
	 * Transform nurses data into options format for react-select
	 * Maps each nurse to an option with their ID as value and name as label
	 */
	const options: SelectOption<number>[] = nurses.list.map(({ id, user: { name } }) => ({
		value: id,
		label: name,
	}));

	/**
	 * Local state for the selected option
	 * Initialized from the value prop if provided
	 */
	const [option, setOption] = useState<SelectOption<number> | null>(
		typeof value !== undefined ? options.find((option) => option.value === value)! : null
	);

	/**
	 * Handle selection change in the dropdown
	 * Updates both local state and parent component state
	 *
	 * @param option - The newly selected option or null if cleared
	 */
	const handleChange = (option: SelectOption<number> | null) => {
		setOption(option);
		setValue(option?.value);
	};

	/**
	 * Render the react-select dropdown component
	 * Configured with custom styling and behavior options
	 */
	return (
		<Select
			placeholder="Assign a Nurse"
			defaultValue={defaultValue}
			className={classes['react-select-container']}
			classNamePrefix="react-select"
			options={options}
			isClearable={true} // Allow clearing the selection
			isSearchable={true} // Allow searching by typing
			value={option}
			onChange={handleChange}
		/>
	);
};

export default NurseSelect;
